import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import { CheckedFormElement } from '../checked-form-element';
import css from './lib-checkbox.css';

/** A labelled checkbox with native keyboard and accessibility behavior. */
@customElement('lib-checkbox')
export class LibCheckbox extends CheckedFormElement {
  protected override get revisionEventName(): string { return 'lib-checkbox-change'; }
  protected override get hostFocusSelector(): string { return 'input'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css.replaceAll('__TICK_ICON__', new URL('../wago-icons-svg/tick-sign.svg', import.meta.url).href))];

  /** String submitted when checked; the boolean value API is unchanged. */
  @property({ attribute: 'submit-value' }) submitValue = 'on';
  @property({ type: Boolean, reflect: true }) error = false;
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;

  /** Exposes the boolean value to delegated change event listeners. */
  get value(): boolean { return this.checked; }
  protected override get submissionValue(): string { return this.submitValue; }

  override render() {
    return html`<label class="lib-checkbox ${this.effectivelyDisabled ? 'lib-checkbox-disabled' : ''} ${this.error ? 'lib-checkbox-error' : ''}">
      <input class="lib-checkbox-input" type="checkbox" .checked=${this.checked}
        ?disabled=${this.effectivelyDisabled} aria-invalid=${this.error ? 'true' : nothing}
        aria-label=${this.ariaLabel || nothing} @change=${this.#change}>
      <span class="lib-checkbox-box" aria-hidden="true"></span>
      <span class="lib-checkbox-text"><span class="lib-checkbox-label"><slot></slot></span></span>
    </label>`;
  }

  #change(event: Event): void {
    event.stopPropagation();
    if (this.effectivelyDisabled) return;
    this.revision++;
    this.checked = (event.target as HTMLInputElement).checked;
    this.syncFormValue();
    this.notifyChange();
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-checkbox': LibCheckbox; }
}
